import { useEffect, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { FieldRow, FieldCheckboxRow, blurOnEnter, emptyToUndefined } from "./Field";

/**
 * Behavior section — defaultValue, forceUppercase, dateFormat,
 * verificationPolicy.
 *
 * Conditional rendering:
 *   - dateFormat — shown when dataType=date
 *   - forceUppercase — shown when dataType=text (uppercasing only makes
 *     sense for free-text fields)
 *
 * Modal-redesign WU-C (2026-05-10) — the Required toggle moved to
 * ValidationSection (lives with the other validation properties). Required is
 * now just the `isRequired` field flag; validation derives from it (Lane C
 * Part 2 removed the old validate(required) behavior sync).
 */

const DATE_FORMATS = [
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (default)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { value: "YYYY/MM/DD", label: "YYYY/MM/DD" },
  { value: "M/D/YYYY", label: "M/D/YYYY" },
] as const;

const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

const VERIFICATION_POLICIES: ReadonlyArray<{
  value: NonNullable<FieldContent["verificationPolicy"]>;
  label: string;
}> = [
  // E-022 glossary — these are REVIEW-disposition values (stop-for-review),
  // NOT validation. Labels are reworded to avoid the "verify"/validate
  // collision. Stored enum values are unchanged.
  // F-046 — honest label: a value with NO OCR confidence (hand-keyed,
  // unrecognized) also stops under "auto"; the old wording ("only if
  // low-confidence") read as a contradiction at second-touch steps.
  // E-058↔E-022 glossary pairing — "low-confidence" is named by its
  // mechanism (the confidence floor), so this option and the Calibration
  // tab's floors visibly describe the same thing.
  { value: "auto", label: "Stop if below its confidence floor, not OCR-read, or a rule fails" },
  { value: "always-verify", label: "Always stop for review" },
  { value: "skip", label: "Never stop / auto-accept" },
];

export function BehaviorSection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;

  const [defaultValueDraft, setDefaultValueDraft] = useState(field?.defaultValue ?? "");

  useEffect(() => {
    setDefaultValueDraft(field?.defaultValue ?? "");
  }, [field?.fieldDefinitionId, field?.defaultValue]);

  if (!field || !content) return null;

  function commitDefaultValue() {
    if (!field) return;
    const next = emptyToUndefined(defaultValueDraft);
    if (next === field.defaultValue) return;
    updateField(field.name, { defaultValue: next });
  }

  function toggleForceUppercase(next: boolean) {
    if (!field) return;
    updateField(field.name, { forceUppercase: next });
  }

  function commitDateFormat(next: string) {
    if (!field) return;
    if (next === (field.dateFormat ?? DEFAULT_DATE_FORMAT)) return;
    updateField(field.name, { dateFormat: next });
  }

  function commitVerificationPolicy(next: NonNullable<FieldContent["verificationPolicy"]>) {
    if (!field) return;
    if (next === (field.verificationPolicy ?? "auto")) return;
    updateField(field.name, { verificationPolicy: next });
  }

  const showDateFormat = field.dataType === "date";
  const showForceUppercase = field.dataType === "text";

  return (
    <section className="field-section" aria-label="Behavior">
      <h3 className="field-section__title">Behavior</h3>

      <FieldRow
        htmlFor="behav-default-value"
        label="Default value"
        hint="Pre-fills empty fields when documents arrive."
      >
        <input
          id="behav-default-value"
          type="text"
          className="field-section__input"
          value={defaultValueDraft}
          onChange={(e) => setDefaultValueDraft(e.target.value)}
          onBlur={commitDefaultValue}
          onKeyDown={blurOnEnter}
        />
      </FieldRow>

      {showForceUppercase && (
        <FieldCheckboxRow
          id="behav-force-uppercase"
          checked={field.forceUppercase ?? false}
          label="Force uppercase"
          hint="Overrides the job type's force-uppercase setting for this field. Unchecked = inherit."
          onChange={toggleForceUppercase}
        />
      )}

      {showDateFormat && (
        <FieldRow
          htmlFor="behav-date-format"
          label="Date format"
          hint={
            <>
              How operators key and exports render dates.{" "}
              <code>YYYY-MM-DD</code> (ISO), <code>MM/DD/YYYY</code> (US),{" "}
              <code>DD/MM/YYYY</code> (EU).
            </>
          }
        >
          <select
            id="behav-date-format"
            className="field-section__input"
            value={field.dateFormat ?? DEFAULT_DATE_FORMAT}
            onChange={(e) => commitDateFormat(e.target.value)}
          >
            {DATE_FORMATS.map((df) => (
              <option key={df.value} value={df.value}>{df.label}</option>
            ))}
          </select>
        </FieldRow>
      )}

      <FieldRow
        htmlFor="behav-verification-policy"
        label="Review disposition (verification policy)"
        hint="When the verification station STOPS this field for a human. Independent of validation — the field is always validated; this only controls whether the operator must review it. The confidence floor comes from the job type (Calibration tab) unless this field overrides it under Validation."
      >
        <select
          id="behav-verification-policy"
          className="field-section__input"
          value={field.verificationPolicy ?? "auto"}
          onChange={(e) => commitVerificationPolicy(e.target.value as NonNullable<FieldContent["verificationPolicy"]>)}
        >
          {VERIFICATION_POLICIES.map((vp) => (
            <option key={vp.value} value={vp.value}>{vp.label}</option>
          ))}
        </select>
      </FieldRow>

    </section>
  );
}
